import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bar } from 'react-chartjs-2'
import {
    FaArrowRight,
    FaChartBar,
    FaExternalLinkAlt,
    FaGlobe,
    FaSpinner,
    FaUserCheck,
    FaUsersCog,
} from 'react-icons/fa'
import type { AxiosError } from 'axios'
import api from '../../../../helpers/apiRequester'
import { brandPalette, formatNumberGBP } from '../chartHelpers'
import styles from '../styles.module.scss'

interface IntroducerSummary {
    key: string
    name: string
    route: string
    clientsSigned: number | null
    claimsFound: number | null
}

interface CachedSummary {
    introducers: IntroducerSummary[]
    fetchedAt: number
}

const FALLBACK_INTRODUCERS: IntroducerSummary[] = [
    { key: 'sentinel', name: 'Sentinel', route: '/sentinel-home', clientsSigned: null, claimsFound: null },
    { key: 'adl', name: 'ADL', route: '/adl-home', clientsSigned: null, claimsFound: null },
    { key: 'pcpmo26', name: 'PCPMO26', route: '/pcpmo26-home', clientsSigned: null, claimsFound: null },
    {
        key: 'sentinelMatters',
        name: 'SentinelMatters',
        route: '/sentinel-matters-home',
        clientsSigned: null,
        claimsFound: null,
    },
    {
        key: 'pcpReferral',
        name: 'PCP Referral',
        route: '/pcp-referral/overview',
        clientsSigned: null,
        claimsFound: null,
    },
    { key: 'mbfcheck', name: 'MBFCheck', route: '/mbfcheck-home', clientsSigned: null, claimsFound: null },
    { key: 'mfc', name: 'Evans Hughes', route: '/mfc-home', clientsSigned: null, claimsFound: null },
    { key: 'mail', name: 'Check Finance', route: '/mail-referral-home', clientsSigned: null, claimsFound: null },
    { key: 'fairclaim', name: 'Claim Delivery API', route: '/fairclaim-home', clientsSigned: null, claimsFound: null },
]

// Lead-capture URLs each introducer points clients at. Hard-coded by design — these
// rarely change and aren't worth fetching server-side just for display.
const INTRODUCER_CAPTURE_URLS: Record<string, string> = {
    sentinel: 'claim.sentinellegal.co.uk',
    adl: 'portal.sentinellegal.co.uk',
    pcpmo26: 'casematters.sentinellegal.co.uk',
    sentinelMatters: 'mail.sentinellegal.co.uk',
    pcpReferral: 'portal.sentinellegal.co.uk',
    mbfcheck: 'mbfinance-check.sentinellegal.co.uk',
    mail: 'checkfinance.sentinellegal.co.uk',
    mfc: 'mfc-advice.sentinellegal.co.uk',
    // FairClaim capture their own leads and deliver them to us by API, so this
    // is their site rather than one of ours.
    fairclaim: 'claimpcp.today',
}

// Adding an introducer no longer needs a bump: cached values are merged onto
// the code-defined list (see mergeCachedValues), so a cached payload missing a
// key can't hide its tile. Bump only if the cached *shape* changes.
const CACHE_KEY = 'sentinel.dashboard.introducerSummary.v5'

// Per-request ceiling for the introducer fan-out. The shared axios instance
// defaults to ~83 minutes, which is effectively no timeout: a single stalled
// overview would leave the section spinning forever, indistinguishable from a
// slow-but-working load. Generous on purpose — the browser caps concurrent
// connections to the API origin at ~6 and starts the XHR timer when the
// request is queued, not when it hits the wire, so the last tiles dispatched
// must absorb the queue wait on top of their own 30s+ runtime.
const INTRODUCER_REQUEST_TIMEOUT_MS = 150_000

const readCache = (): CachedSummary | null => {
    if (typeof window === 'undefined') return null
    try {
        const raw = window.localStorage.getItem(CACHE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw) as CachedSummary
        if (!parsed || !Array.isArray(parsed.introducers)) return null
        return parsed
    } catch {
        return null
    }
}

const writeCache = (introducers: IntroducerSummary[]) => {
    if (typeof window === 'undefined') return
    try {
        const payload: CachedSummary = { introducers, fetchedAt: Date.now() }
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(payload))
    } catch {
        // Quota or serialization failures are non-fatal; we just skip caching.
    }
}

// The tile set always comes from FALLBACK_INTRODUCERS; the cache only supplies
// previously-fetched values. Adopting the cached list wholesale meant a payload
// written before an introducer existed hid that tile permanently — the fan-out
// maps over the current entries and never appends, so the new key's response
// was discarded on every load, and the partial-result write then re-persisted
// the short list. Names and routes are taken from code too, so renaming an
// introducer isn't masked by a stale cached label.
const mergeCachedValues = (cachedList?: IntroducerSummary[]): IntroducerSummary[] => {
    if (!cachedList?.length) return FALLBACK_INTRODUCERS
    const cachedByKey = new Map(cachedList.map((i) => [i.key, i]))
    return FALLBACK_INTRODUCERS.map((introducer) => {
        const hit = cachedByKey.get(introducer.key)
        return hit ? { ...introducer, clientsSigned: hit.clientsSigned, claimsFound: hit.claimsFound } : introducer
    })
}

const formatRelative = (timestamp: number): string => {
    const diffMs = Date.now() - timestamp
    if (diffMs < 60_000) return 'just now'
    const minutes = Math.floor(diffMs / 60_000)
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
    const days = Math.floor(hours / 24)
    return `${days} day${days === 1 ? '' : 's'} ago`
}

export const PCPIntroducersSection = () => {
    const navigate = useNavigate()
    const cached = useMemo(readCache, [])

    const [introducers, setIntroducers] = useState<IntroducerSummary[]>(() => mergeCachedValues(cached?.introducers))
    const [loadedKeys, setLoadedKeys] = useState<Set<string>>(() => new Set())
    const [refreshing, setRefreshing] = useState<boolean>(true)
    const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(cached?.fetchedAt ?? null)
    const [error, setError] = useState<string | null>(null)

    // Mirror of `introducers` so the post-fanout cache write can read the freshest
    // state without forcing the effect to re-run.
    const introducersRef = useRef(introducers)
    useEffect(() => {
        introducersRef.current = introducers
    }, [introducers])

    // Persist partial results as each tile lands, not just when the whole
    // fan-out settles. The slowest overviews take 30s+, and a refresh mid-load
    // used to throw away everything that had already returned.
    useEffect(() => {
        if (loadedKeys.size === 0) return
        writeCache(introducers)
    }, [introducers, loadedKeys])

    useEffect(() => {
        let cancelled = false

        // Dispatch order, not display order. The browser caps concurrent
        // requests to the API origin at ~6, and some overview requests take
        // 30s+, so anything after the first six sits queued behind them.
        // Known-fast keys (count-query backed) go first so their tiles aren't
        // stuck waiting on the slowest introducers.
        const FAST_KEYS = ['fairclaim']
        const keys = [...FALLBACK_INTRODUCERS.map((i) => i.key)].sort(
            (a, b) => Number(FAST_KEYS.includes(b)) - Number(FAST_KEYS.includes(a)),
        )
        let anyFailure = false
        let anyTimeout = false

        const fetchOne = async (key: string) => {
            try {
                const response = await api({
                    url: '/dashboard-introducer-summary-one',
                    data: { key },
                    timeout: INTRODUCER_REQUEST_TIMEOUT_MS,
                })
                if (cancelled) return
                const fresh: IntroducerSummary | undefined = response.data?.introducer
                if (response.data?.success && fresh) {
                    setIntroducers((prev) => prev.map((i) => (i.key === key ? { ...i, ...fresh } : i)))
                    setLoadedKeys((prev) => {
                        const next = new Set(prev)
                        next.add(key)
                        return next
                    })
                    setError(null)
                } else {
                    anyFailure = true
                    // Still mark the tile as "loaded" so it doesn't stay as a permanent
                    // skeleton. It will render whatever value it already had (cache or null).
                    setLoadedKeys((prev) => {
                        const next = new Set(prev)
                        next.add(key)
                        return next
                    })
                }
            } catch (err) {
                if (cancelled) return
                anyFailure = true
                if ((err as AxiosError)?.code === 'ECONNABORTED') anyTimeout = true
                setLoadedKeys((prev) => {
                    const next = new Set(prev)
                    next.add(key)
                    return next
                })
            }
        }

        Promise.allSettled(keys.map(fetchOne)).then(() => {
            if (cancelled) return
            setLastFetchedAt(Date.now())
            setRefreshing(false)
            writeCache(introducersRef.current)
            if (anyFailure) {
                setError(
                    (prev) =>
                        prev ??
                        (anyTimeout
                            ? 'Some introducer figures timed out and are showing their last known values'
                            : 'Some introducer figures could not be refreshed'),
                )
            }
        })

        return () => {
            cancelled = true
        }
    }, [])

    const hasCachedData = !!cached
    const showingStale = refreshing && hasCachedData

    // Which keys the cache actually carried a value for. A newly-added
    // introducer has no cached value even when a cache exists, and should show
    // a skeleton rather than pulse over a placeholder "n/a".
    const cachedKeys = useMemo(() => new Set((cached?.introducers ?? []).map((i) => i.key)), [cached])
    const hasFreshData = loadedKeys.size > 0

    const barData = useMemo(
        () => ({
            labels: introducers.map((i) => i.name),
            datasets: [
                {
                    label: 'Clients Signed',
                    data: introducers.map((i) => i.clientsSigned ?? 0),
                    backgroundColor: brandPalette[0],
                    borderRadius: 6,
                },
                {
                    label: 'Claims Found',
                    data: introducers.map((i) => i.claimsFound ?? 0),
                    backgroundColor: brandPalette[3],
                    borderRadius: 6,
                },
            ],
        }),
        [introducers],
    )

    const hasAnyData = introducers.some((i) => (i.clientsSigned ?? 0) > 0 || (i.claimsFound ?? 0) > 0)

    return (
        <section className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
                <div>
                    <h2 className={styles.sectionTitle}>
                        <FaUsersCog /> PCP Introducers
                    </h2>
                    <p className={styles.sectionSubtitle}>Headline signatures and claims across each introducer.</p>
                </div>
            </div>

            {error && hasFreshData ? <div className={styles.errorBanner}>{error}</div> : null}

            <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                    <FaChartBar /> Clients Signed vs Claims Found
                </div>
                <div className={`${styles.chartCanvas} ${styles.chartCanvasTall}`}>
                    {hasAnyData ? (
                        <Bar
                            data={barData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { position: 'top' },
                                    tooltip: {
                                        callbacks: {
                                            label: (ctx) =>
                                                `${ctx.dataset.label}: ${formatNumberGBP(Number(ctx.parsed.y) || 0)}`,
                                        },
                                    },
                                },
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                        ticks: {
                                            callback: (value) =>
                                                typeof value === 'number' ? formatNumberGBP(value) : value,
                                        },
                                    },
                                },
                            }}
                        />
                    ) : (
                        <div className={styles.chartPlaceholder}>
                            {refreshing ? (
                                <span className={styles.placeholderLoading}>
                                    <FaSpinner className={styles.spinner} aria-hidden="true" />
                                    Loading chart data&hellip;
                                </span>
                            ) : (
                                'No introducer data available'
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.introducerGrid}>
                {introducers.map((introducer) => {
                    const captureUrl = INTRODUCER_CAPTURE_URLS[introducer.key]
                    const captureHref = captureUrl ? `https://${captureUrl}` : null
                    const tileLoaded = loadedKeys.has(introducer.key)
                    // Show a skeleton only when we have nothing to display for this
                    // specific tile yet (no fresh value AND no cached value).
                    const tileCached = cachedKeys.has(introducer.key)
                    const tileSkeleton = !tileLoaded && !tileCached
                    const tilePulsing = !tileLoaded && tileCached
                    return (
                        <div
                            key={introducer.key}
                            className={`${styles.introducerCard} ${
                                tileSkeleton ? styles.introducerCardLoading : ''
                            } ${tilePulsing ? styles.introducerCardPulsing : ''}`}
                            role="button"
                            tabIndex={tileSkeleton ? -1 : 0}
                            onClick={() => {
                                if (tileSkeleton) return
                                navigate(introducer.route)
                            }}
                            onKeyDown={(e) => {
                                if (tileSkeleton) return
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    navigate(introducer.route)
                                }
                            }}
                        >
                            <div className={styles.introducerName}>
                                <FaUserCheck /> {introducer.name}
                            </div>
                            {captureUrl && captureHref ? (
                                <div
                                    className={styles.introducerCaptureUrl}
                                    onClick={(e) => e.stopPropagation()}
                                    tabIndex={0}
                                >
                                    <FaGlobe aria-hidden="true" />
                                    <span className={styles.captureUrlText}>{captureUrl}</span>
                                    <div className={styles.introducerCaptureTooltip} role="tooltip">
                                        <span className={styles.captureTooltipLabel}>Lead capture URL</span>
                                        <span className={styles.captureTooltipUrl}>{captureHref}</span>
                                        <a
                                            className={styles.captureTooltipOpen}
                                            href={captureHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Open <FaExternalLinkAlt aria-hidden="true" />
                                        </a>
                                    </div>
                                </div>
                            ) : null}
                            <div className={styles.introducerStats}>
                                <div>
                                    <span className={styles.introducerLabel}>Signed</span>
                                    <span className={styles.introducerValue}>
                                        {tileSkeleton ? (
                                            <span className={styles.skeletonValue} aria-label="Loading" />
                                        ) : introducer.clientsSigned == null ? (
                                            'n/a'
                                        ) : (
                                            formatNumberGBP(introducer.clientsSigned)
                                        )}
                                    </span>
                                </div>
                                <div>
                                    <span className={styles.introducerLabel}>Claims</span>
                                    <span className={styles.introducerValue}>
                                        {tileSkeleton ? (
                                            <span className={styles.skeletonValue} aria-label="Loading" />
                                        ) : introducer.claimsFound == null ? (
                                            'n/a'
                                        ) : (
                                            formatNumberGBP(introducer.claimsFound)
                                        )}
                                    </span>
                                </div>
                            </div>
                            <span className={styles.introducerLink}>
                                View stats <FaArrowRight />
                            </span>
                        </div>
                    )
                })}
            </div>

            {refreshing ? (
                <div className={styles.loadingNoticeFooter} role="status" aria-live="polite">
                    <FaSpinner className={styles.spinner} aria-hidden="true" />
                    <span className={styles.loadingTitle}>
                        {showingStale ? 'Refreshing introducer figures' : 'Loading introducer figures'}
                    </span>
                    <span className={styles.loadingSubtitle}>
                        {showingStale && lastFetchedAt != null
                            ? `Showing values from ${formatRelative(lastFetchedAt)} while the latest data loads.`
                            : 'This is a lot of data and can take up to a minute.'}
                    </span>
                </div>
            ) : lastFetchedAt != null ? (
                <div className={styles.loadingNoticeFooter}>
                    <span className={styles.loadingSubtitle}>Last refreshed {formatRelative(lastFetchedAt)}.</span>
                </div>
            ) : null}
        </section>
    )
}
